import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Radio, Zap, Info, ArrowRight, ArrowLeft, Sparkles, CheckCircle2, BookOpen, Image as ImageIcon, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { UploadableFileInput } from './common/UploadableFileInput';

type LessonMode = 'live' | 'student-paced' | null;

interface CreateLessonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateLesson: (lesson: {
    title: string;
    description: string;
    mode: LessonMode;
    coverImage?: string;
  }) => void;
}

const TITLE_MAX_LENGTH = 80;
const DESC_MAX_LENGTH = 300;

export function CreateLessonModal({ open, onOpenChange, onCreateLesson }: CreateLessonModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<LessonMode>(null);
  const [coverImage, setCoverImage] = useState<string>('');
  const [step, setStep] = useState(1);
  const [showError, setShowError] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus title input when modal opens
  useEffect(() => {
    if (open && step === 1) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [open, step]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setTitle('');
      setDescription('');
      setMode(null);
      setCoverImage('');
      setStep(1);
      setShowError(false);
      setIsCreating(false);
    }
  }, [open]);

  const handleNext = () => {
    if (step === 1) {
      if (!title.trim()) {
        toast.error('Please enter a lesson title');
        titleInputRef.current?.focus();
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!mode) {
        setShowError(true);
        toast.error('Please select a lesson mode');
        return;
      }
      handleCreate();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setShowError(false);
    }
  };

  const handleCreate = () => {
    setShowError(false);

    if (!title.trim()) {
      toast.error('Please enter a lesson title');
      return;
    }

    if (!mode) {
      setShowError(true);
      toast.error('Please select a lesson mode');
      return;
    }

    setIsCreating(true);

    onCreateLesson({
      title,
      description,
      mode,
      coverImage,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNext();
    }
  };

  const steps = [
    { number: 1, label: 'Details', icon: BookOpen },
    { number: 2, label: 'Mode', icon: Layers },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-2xl border-border/50 shadow-2xl">
        {/* Header with gradient accent */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-primary/4 to-transparent" />
          <div className="relative px-8 pt-8 pb-4">
            <DialogHeader className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold tracking-tight">Create New Lesson</DialogTitle>
                  <DialogDescription className="text-muted-foreground mt-0.5">
                    {step === 1 ? 'Set up your lesson details and cover image' : 'Choose how students will experience this lesson'}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mt-6">
              {steps.map((s, i) => (
                <div key={s.number} className="flex items-center gap-2 flex-1">
                  <button
                    onClick={() => {
                      if (s.number < step) setStep(s.number);
                    }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                      step === s.number
                        ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25'
                        : step > s.number
                        ? 'bg-primary/10 text-primary cursor-pointer hover:bg-primary/20'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {step > s.number ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <s.icon className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="sm:hidden">{s.number}</span>
                  </button>
                  {i < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 rounded-full transition-colors duration-500 ${
                      step > s.number ? 'bg-primary/40' : 'bg-border'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="px-8 py-6">
          {/* Step 1: Lesson Details */}
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              {/* Lesson Title */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="title" className="text-sm font-semibold">Lesson Title <span className="text-red-500">*</span></Label>
                  <span className={`text-xs transition-colors ${
                    title.length > TITLE_MAX_LENGTH ? 'text-red-500 font-medium' : 'text-muted-foreground'
                  }`}>
                    {title.length}/{TITLE_MAX_LENGTH}
                  </span>
                </div>
                <Input
                  ref={titleInputRef}
                  id="title"
                  placeholder="e.g., Introduction to Photosynthesis"
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX_LENGTH))}
                  onKeyDown={handleKeyDown}
                  className="h-12 rounded-xl text-base border-border/60 focus:border-primary/50 transition-colors"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="description" className="text-sm font-semibold">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <span className={`text-xs transition-colors ${
                    description.length > DESC_MAX_LENGTH ? 'text-red-500 font-medium' : 'text-muted-foreground'
                  }`}>
                    {description.length}/{DESC_MAX_LENGTH}
                  </span>
                </div>
                <Textarea
                  id="description"
                  placeholder="Brief description of what students will learn..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, DESC_MAX_LENGTH))}
                  className="rounded-xl min-h-[100px] border-border/60 focus:border-primary/50 transition-colors resize-none"
                />
              </div>

              {/* Cover Image */}
              <div className="space-y-2">
                <UploadableFileInput
                  label="Cover Image"
                  currentFileUrl={coverImage}
                  onFileUpload={setCoverImage}
                  onFileRemove={() => setCoverImage('')}
                  acceptedTypes="images"
                />
                {!coverImage && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5" />
                    A cover image makes your lesson more recognizable
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Lesson Mode Selection */}
          {step === 2 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-sm font-semibold">Choose Lesson Mode <span className="text-red-500">*</span></Label>
                <div className="group relative">
                  <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                  <div className="absolute left-0 top-6 w-72 p-4 bg-popover border border-border rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                    <p className="text-xs text-foreground leading-relaxed">
                      <strong className="text-primary">Live Session:</strong> Real-time teaching where you control the pace. Best for in-class instruction.
                      <br /><br />
                      <strong className="text-blue-600">Student-Paced:</strong> Students complete independently on their own schedule. Great for homework or review.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Live Session Option */}
                <button
                  type="button"
                  onClick={() => { setMode('live'); setShowError(false); }}
                  className={`group/card relative p-6 rounded-2xl border-2 text-left transition-all duration-300 ${
                    mode === 'live'
                      ? 'border-emerald-500 bg-emerald-50/80 dark:bg-emerald-950/30 shadow-lg shadow-emerald-500/15 scale-[1.02]'
                      : showError && !mode
                      ? 'border-red-400 bg-red-50/50 dark:bg-red-950/20'
                      : 'border-border bg-background hover:border-emerald-300 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/10 hover:shadow-md'
                  }`}
                >
                  {mode === 'live' && (
                    <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center animate-in zoom-in duration-200">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div className="flex flex-col gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      mode === 'live'
                        ? 'bg-emerald-500 shadow-md shadow-emerald-500/30'
                        : 'bg-emerald-100 dark:bg-emerald-900/40 group-hover/card:bg-emerald-200 dark:group-hover/card:bg-emerald-900/60'
                    }`}>
                      <Zap className={`w-7 h-7 transition-colors duration-300 ${
                        mode === 'live' ? 'text-white' : 'text-emerald-600 dark:text-emerald-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1.5">Live Session</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Teacher controls slides in real-time. Students follow along live together.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-medium">Real-time</span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-medium">In-class</span>
                    </div>
                  </div>
                </button>

                {/* Student-Paced Option */}
                <button
                  type="button"
                  onClick={() => { setMode('student-paced'); setShowError(false); }}
                  className={`group/card relative p-6 rounded-2xl border-2 text-left transition-all duration-300 ${
                    mode === 'student-paced'
                      ? 'border-blue-500 bg-blue-50/80 dark:bg-blue-950/30 shadow-lg shadow-blue-500/15 scale-[1.02]'
                      : showError && !mode
                      ? 'border-red-400 bg-red-50/50 dark:bg-red-950/20'
                      : 'border-border bg-background hover:border-blue-300 hover:bg-blue-50/40 dark:hover:bg-blue-950/10 hover:shadow-md'
                  }`}
                >
                  {mode === 'student-paced' && (
                    <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center animate-in zoom-in duration-200">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div className="flex flex-col gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      mode === 'student-paced'
                        ? 'bg-blue-500 shadow-md shadow-blue-500/30'
                        : 'bg-blue-100 dark:bg-blue-900/40 group-hover/card:bg-blue-200 dark:group-hover/card:bg-blue-900/60'
                    }`}>
                      <Radio className={`w-7 h-7 transition-colors duration-300 ${
                        mode === 'student-paced' ? 'text-white' : 'text-blue-600 dark:text-blue-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1.5">Student-Paced</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Students complete the lesson at their own pace and schedule.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">Self-paced</span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">Homework</span>
                    </div>
                  </div>
                </button>
              </div>

              {showError && !mode && (
                <p className="text-sm text-red-600 flex items-center gap-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                  <Info className="w-4 h-4" />
                  Please select a lesson mode to continue
                </p>
              )}

              {/* Preview Summary */}
              {mode && (
                <div className="mt-4 p-4 rounded-xl bg-muted/50 border border-border/60 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Lesson Preview</h4>
                  <div className="flex items-start gap-3">
                    {coverImage ? (
                      <img src={coverImage} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-6 h-6 text-primary/60" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{title}</p>
                      {description && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md ${
                          mode === 'live'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}>
                          {mode === 'live' ? <Zap className="w-3 h-3" /> : <Radio className="w-3 h-3" />}
                          {mode === 'live' ? 'Live Session' : 'Student-Paced'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-8 py-5 border-t border-border/60 bg-muted/30">
          <div>
            {step > 1 ? (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="rounded-xl text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="rounded-xl text-muted-foreground hover:text-foreground"
              >
                Cancel
              </Button>
            )}
          </div>
          <Button
            onClick={handleNext}
            disabled={isCreating}
            className={`rounded-xl px-6 h-11 font-semibold shadow-md transition-all duration-300 ${
              step === 2 && mode
                ? 'bg-primary hover:bg-primary/90 shadow-primary/25 hover:shadow-lg hover:shadow-primary/30'
                : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {isCreating ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Creating...
              </>
            ) : step === 2 ? (
              <>
                <Sparkles className="w-4 h-4 mr-1.5" />
                Create Lesson
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}